import React, { useState } from 'react';
import { RmButton, RmCard, RmEditorHeader, RmEditorPage, RmFixList, RmFooter } from './EditorShell';
import { RmStepsColumn } from './RmStepsColumn';
import { RmStepEditor } from './RmStepEditor';
import { Icon } from '../Icon/Icon';
import { POST_OP_PATIENTS, REMINDERS } from '../../settings-reminders/mockData';
import { sectionIssues } from '../../settings-reminders/derive';
import type { RmChannel, RmSection, RmStep } from '../../settings-reminders/types';

export interface RmMessagesViewProps {
  section: RmSection;
  onChange: (next: RmSection) => void;
  onBack: () => void;
  onContinue: () => void;
  onOpenAppointmentReasons?: (() => void) | undefined;
  /** A fix link on the test page names a step; this opens it (spec 23 §4.2). */
  initialStepId?: string | undefined;
}

/**
 * Step 1 — Messages (amendment 25 §3, boards `RM1Before` and `RM4PostOp`).
 *
 * **One component for all four kinds of section.** Before-the-visit, a
 * procedure's post-op checks, the missed-appointment follow-up and the review ask
 * differ in their nouns, their field menu and whether they have a complication
 * card — not in their shape. Four components would be four places for the same
 * bug.
 */
export function RmMessagesView({
  section,
  onChange,
  onBack,
  onContinue,
  onOpenAppointmentReasons,
  initialStepId,
}: RmMessagesViewProps) {
  const [selectedId, setSelectedId] = useState<string | undefined>(
    (initialStepId !== undefined && section.steps.some((s) => s.id === initialStepId) ? initialStepId : undefined) ??
      section.steps[0]?.id,
  );

  const issues = sectionIssues(section);
  const index = section.steps.findIndex((s) => s.id === selectedId);
  const selected = section.steps[index];
  const postOp = section.kind === 'post_op';

  const setSteps = (steps: RmStep[]) => {
    onChange({ ...section, steps });
  };

  const addStep = (channel: RmChannel) => {
    const last = section.steps[section.steps.length - 1];
    const step: RmStep = {
      id: `rm-new-${String(Date.now())}`,
      channel,
      offset: (last?.offset ?? 0) + (postOp ? 3 : -1) > 0 ? (last?.offset ?? 1) + (postOp ? 3 : 0) : 1,
      unit: last?.unit ?? 'days',
      mode: channel === 'ai_call' ? 'smart' : 'template',
      on: true,
      audience: channel === 'ai_call' && section.kind === 'before' ? 'not_confirmed' : 'everyone',
    };
    setSteps([...section.steps, step]);
    setSelectedId(step.id);
  };

  return (
    <RmEditorPage>
      <RmEditorHeader
        active="messages"
        crumb={`Settings › Appointment Reminders › ${section.title}`}
        titleLead={section.titleLead}
        titleAccent={section.titleAccent}
        sub={section.sub}
        onGoTo={(page) => {
          if (page === 'test') onContinue();
        }}
      />

      {/* The complication card sits ABOVE the checks, because it applies to all of
          them — one list per procedure, named on every check (§5). */}
      {postOp ? (
        <div className="mt-5">
          <ComplicationCard section={section} onChange={onChange} />
        </div>
      ) : null}

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        <RmStepsColumn
          section={section}
          selectedId={selectedId}
          issues={issues}
          onSelect={setSelectedId}
          onAdd={addStep}
        />

        <div className="min-w-0 flex-1">
          {selected === undefined ? (
            <div className="rounded-[16px] border border-dashed border-[var(--border-default)] px-6 py-12 text-center text-[13px] text-[var(--text-muted)]">
              Add a {postOp ? 'check' : 'step'} to start writing.
            </div>
          ) : (
            <RmStepEditor
              section={section}
              step={selected}
              index={index < 0 ? 0 : index}
              appointments={postOp ? POST_OP_PATIENTS : REMINDERS.previewAppointments}
              onChange={(next) => {
                setSteps(section.steps.map((s) => (s.id === next.id ? next : s)));
              }}
              onRemove={() => {
                const rest = section.steps.filter((s) => s.id !== selected.id);
                setSteps(rest);
                setSelectedId(rest[0]?.id);
              }}
              onOpenAppointmentReasons={onOpenAppointmentReasons}
            />
          )}
        </div>
      </div>

      <RmFooter left={<RmFixList issues={issues} onGoToStep={setSelectedId} />}>
        <RmButton onClick={onBack}>Back to settings</RmButton>
        <RmButton kind="primary" onClick={onContinue}>
          Continue to test
        </RmButton>
      </RmFooter>
    </RmEditorPage>
  );
}

/**
 * *What to watch for after {procedure}* (§5).
 *
 * **One list per procedure, and there must not be a global one**: *difficulty
 * swallowing* is an emergency after a lower extraction and nothing after a crown
 * seat. A shared list would be wrong for every procedure on it.
 */
function ComplicationCard({
  section,
  onChange,
}: {
  section: RmSection;
  onChange: (next: RmSection) => void;
}) {
  const [adding, setAdding] = useState('');
  const list = section.complications ?? [];
  return (
    <RmCard
      tone="red"
      title={`What to watch for after ${article(section.title)} ${section.title.toLowerCase()}`}
      sub={
        <>
          Every check for this procedure names these. If a patient mentions one, or scores their pain 7 or more,{' '}
          <b className="font-semibold text-[var(--text-secondary)]">ELVA hands them to a person straight away.</b>
        </>
      }
    >
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {list.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1.5 rounded-[7px] bg-[#FCEDED] px-2 py-1 text-[11.5px] font-semibold text-[var(--alert-red)]"
          >
            {c}
            <button
              type="button"
              aria-label={`Remove ${c}`}
              onClick={() => {
                onChange({ ...section, complications: list.filter((x) => x !== c) });
              }}
              className="opacity-70 hover:opacity-100"
            >
              <Icon kind="x" size={10} stroke={2.4} />
            </button>
          </span>
        ))}
        <span className="inline-flex items-center gap-1">
          <input
            value={adding}
            placeholder="Add one"
            aria-label="Add a complication to watch for"
            onChange={(e) => {
              setAdding(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && adding.trim() !== '') {
                onChange({ ...section, complications: [...list, adding.trim()] });
                setAdding('');
              }
            }}
            className="h-7 w-[150px] rounded-[8px] border border-dashed border-[var(--border-default)] px-2 text-[11.5px] outline-none focus:border-[var(--alert-red)]"
          />
          <button
            type="button"
            disabled={adding.trim() === ''}
            onClick={() => {
              onChange({ ...section, complications: [...list, adding.trim()] });
              setAdding('');
            }}
            className="text-[11.5px] font-semibold text-[var(--brand-indigo)] disabled:text-[var(--text-muted)]"
          >
            + Add
          </button>
        </span>
      </div>
    </RmCard>
  );
}

function article(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}
